import { SearchBar } from "@/components/SearchBar";
import { ExtendedDocInformation, ISharepointInfo } from "./";
import {
  ChevronRight,
  CircleCheck,
  CirclePlus,
  Loader2Icon,
  FileIcon,
} from "lucide-react";
import { NoxusBreadcrumbs } from "@/components/KnowledgeBases/KBBreadcrumbs";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { useCallback, useRef, useMemo, useEffect } from "react";
import { IOneDrivePickerItem } from "../OneDrivePickerField";
import Image from "next/image";
import { getFileImage, formatSizeInBytes, timeAgo } from "@/utils/generic";
import { cn } from "@/lib/utils";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { Button } from "@/components/shadcn/button";

export const OneDriveFileExplorer = ({
  pathHistory,
  setPathHistory,
  activeSharepoint,
  serviceName,
  fetchFiles,
  navigateToBreadcrumb,
  handleFolderClick,
  handleFileClick,
  removeFileFromSelection,
  filteredFiles,
  isLoading,
  tempSelectedItems,
  canSelectDirectories,
  canMultiselect,
  searchQuery,
  setSearchQuery,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: {
  pathHistory: any[];
  setPathHistory: (pathHistory: any[]) => void;
  activeSharepoint: ISharepointInfo | undefined;
  serviceName: string;
  fetchFiles: (params: any) => void;
  navigateToBreadcrumb: (index: number) => void;
  handleFolderClick: (file: ExtendedDocInformation) => void;
  handleFileClick: (file: ExtendedDocInformation) => void;
  removeFileFromSelection: (file: ExtendedDocInformation) => void;
  filteredFiles: ExtendedDocInformation[];
  isLoading: boolean;
  tempSelectedItems: IOneDrivePickerItem[];
  canSelectDirectories: boolean;
  canMultiselect: boolean;
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}) => {
  // Infinite-scroll sentinel for Google Drive pagination
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const el = sentinelRef.current;
    const rootEl = scrollRootRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      // The file list scrolls inside its own container, not the viewport.
      // Pass that container as `root` so intersection is computed against
      // the actual scroll area.
      { root: rootEl ?? null, rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);
  return (
    <div className="flex flex-col h-full bg-background">
      <div className="p-3 pr-12 pl-4 border-b border-border flex-shrink-0 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 w-full min-w-0">
          {pathHistory.length < 4 && (
            <div className="flex gap-2 items-center flex-shrink-0">
              <div
                className={cn(
                  "text-sm font-medium cursor-pointer hover:underline",
                  pathHistory.length === 0
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
                onClick={() => {
                  setPathHistory([]);
                  if (serviceName === "Sharepoint") {
                    fetchFiles({
                      siteId: activeSharepoint?.site?.id,
                      driveId: activeSharepoint?.drive?.id,
                    });
                  } else {
                    fetchFiles({});
                  }
                }}
              >
                {`My data`}
              </div>
              {pathHistory.length > 0 && (
                <ChevronRight
                  size="0.75rem"
                  strokeWidth="1.25px"
                  color="#060C11"
                  style={{ flexShrink: 0 }}
                />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0 truncate">
            <NoxusBreadcrumbs
              entries={pathHistory.map((path) => ({
                value: path.name,
                label: path.name,
              }))}
              onClick={(_, index) => navigateToBreadcrumb(index)}
              onClickRoot={() => {
                setPathHistory([]);
                fetchFiles({});
              }}
            />
          </div>
          <span className="text-xs h-4.5 flex items-end text-muted-foreground whitespace-nowrap">
            {canMultiselect ? "Select multiple" : "Select one"}
          </span>
          <div className="w-56">
            <SearchBar
              value={searchQuery}
              setValue={setSearchQuery}
              isDebounced
            />
          </div>
        </div>
      </div>

      <div ref={scrollRootRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2Icon className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filteredFiles.length === 0 ? (
          <NoxusEmptyBlock
            size="sm"
            icon={FileIcon}
            title="No files found"
            description="Try a different search or location"
          />
        ) : (
          <>
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-background z-10 border-b border-border shadow-sm">
                <tr className="text-xs text-muted-foreground">
                  <th className="font-medium p-3 pl-4">Name</th>
                  <th className="font-medium p-3 w-40">Modified</th>
                  <th className="font-medium p-3 w-28">Size</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file, idx) => (
                  <OneDrivePickerItem
                    key={
                      file.id ??
                      file.drive_metadata?.id ??
                      `${file.title}-${file.content_type}-${idx}`
                    }
                    file={file}
                    tempSelectedItems={tempSelectedItems}
                    canSelectDirectories={canSelectDirectories}
                    handleFolderClick={handleFolderClick}
                    handleFileClick={handleFileClick}
                    removeFileFromSelection={removeFileFromSelection}
                  />
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div
                ref={sentinelRef}
                className="flex items-center justify-center py-3"
              >
                {isLoadingMore ? (
                  <Loader2Icon className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => onLoadMore?.()}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Load more
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const OneDrivePickerItem = ({
  file,
  tempSelectedItems,
  canSelectDirectories,
  handleFolderClick,
  handleFileClick,
  removeFileFromSelection,
}: {
  file: ExtendedDocInformation;
  tempSelectedItems: IOneDrivePickerItem[];
  canSelectDirectories: boolean;
  handleFolderClick: (file: ExtendedDocInformation) => void;
  handleFileClick: (file: ExtendedDocInformation) => void;
  removeFileFromSelection: (file: ExtendedDocInformation) => void;
}) => {
  const fileId = file.drive_metadata?.id || file.drive_id;
  const isItemSelected = useMemo(() => {
    return tempSelectedItems.some((item) => item.id === fileId);
  }, [tempSelectedItems, fileId]);

  const isFolder = file.content_type === "folder";

  // For folders: row click always opens (navigates into) the folder.
  // Selection/deselection is handled by the dedicated icon button.
  const handleClick = useCallback(() => {
    handleFileClick(file);
  }, [file, handleFileClick]);

  const modifiedDateRaw =
    file.drive_metadata?.lastModifiedDateTime || file.modified_at;
  const modifiedDate = modifiedDateRaw ? timeAgo(modifiedDateRaw) : "-";

  const sizeStr = file.drive_metadata?.size
    ? formatSizeInBytes(file.drive_metadata.size)
    : "-";

  const isFileDisabled = false;

  return (
    <tr
      className={cn(
        "group border-b border-border/50 cursor-pointer transition-colors",
        isFileDisabled
          ? "opacity-40 cursor-default"
          : isItemSelected
            ? "bg-blue-50"
            : "hover:bg-muted/50"
      )}
      onClick={isFileDisabled ? undefined : handleClick}
    >
      <td className="p-3 pl-4">
        <div className="flex items-center gap-3">
          <div className="relative w-5 h-5 flex-shrink-0">
            {isFolder ? (
              <Image
                src="https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg"
                alt="Folder"
                fill
                style={{ objectFit: "contain" }}
              />
            ) : (
              <Image
                src={getFileImage(file.content_type)}
                alt="File"
                fill
                style={{ objectFit: "contain" }}
              />
            )}
          </div>
          <NoxusTooltip
            content={file.title?.length >= 30 ? file.title : ""}
            placement="top"
            asChild
          >
            <div className="text-sm font-medium line-clamp-1 break-all flex-1">
              {file.title}
            </div>
          </NoxusTooltip>
          {isFolder &&
            canSelectDirectories &&
            (isItemSelected ? (
              <CircleCheck
                size={16}
                strokeWidth={1.5}
                className="flex-shrink-0 text-primary"
              />
            ) : (
              <NoxusTooltip content="Add folder">
                <button
                  className="flex-shrink-0 rounded-full opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-colors hover:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFolderClick(file);
                  }}
                >
                  <CirclePlus size={16} strokeWidth={1.5} />
                </button>
              </NoxusTooltip>
            ))}
          {!isFolder && isItemSelected && (
            <div className="rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 ml-2">
              <CircleCheck
                size="16"
                strokeWidth="1.5px"
                color="var(--primary)"
              />
            </div>
          )}
        </div>
      </td>
      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
        {modifiedDate}
      </td>
      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
        {!isFolder ? sizeStr : ""}
      </td>
    </tr>
  );
};
